import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';

import {
	BodyTextFieldsFragment,
	EmptyStateFieldsFragment,
	IntroHeaderImageFieldsFragment,
	Maybe
} from 'api/graphql-types';
import { Button, IButtonVariants } from 'components/button';
import { Card, CardDownload, CardNews } from 'components/card';

import { EmptyTab } from './empty-tab';
import styles from '../highlight.module.scss';

type ITabTypename =
	| 'ComponentInternalHighlightNews'
	| 'ComponentInternalHighlightEvents'
	| 'ComponentInternalHighlightSubsidies'
	| 'ComponentInternalHighlightPermits'
	| 'ComponentInternalHighlightMapsAndStats'
	| 'ComponentInternalHighlightTopics'
	| 'ComponentInternalHighlightWoo';

type ITabItemTypename = 'Newsarticle' | 'MapsFigure' | 'Permit' | 'Subsidy' | 'WooRequest';

export interface ITabItem {
	__typename: ITabItemTypename;
	id: string;
	title: string;
	firstPublishedAt?: Maybe<string>;
	createdAt?: Maybe<string>;
	startDate?: Maybe<any>;
	zip?: Maybe<any>;
	slug?: Maybe<any>;
	url?: Maybe<string>;
	basePath?: Maybe<string>;
	header?: Partial<IntroHeaderImageFieldsFragment>;
	bodyText?: BodyTextFieldsFragment;
}

export interface ITab {
	__typename?: ITabTypename;
	id?: string;
	title?: Maybe<string>;
	buttonTitle?: Maybe<string>;
	buttonHref?: Maybe<any>;
	items?: ITabItem[];
	empty?: EmptyStateFieldsFragment | null;
	theme?: {
		id?: string | number;
		title?: string;
	};
}

export const Tab = ({ title, items, theme, buttonTitle, buttonHref, empty }: ITab): React.JSX.Element => {
	const intl = useIntl();
	// Checks if item has images, loads less items when images are found
	const itemsHaveImages = items?.slice(0, 3).some((item) => item.header?.cover);
	const list = itemsHaveImages ? items?.slice(0, 3) : items?.slice(0, 5);

	const [overviewUrl, setOverviewUrl] = useState<undefined | string>(undefined);

	const getOverviewUrl = (): void => {
		if (!items || items.length === 0) return;
		if (items[0].__typename === 'WooRequest') setOverviewUrl('/woo-documenten');
		if (items[0].__typename === 'MapsFigure') setOverviewUrl('/kaarten-en-cijfers');
	};

	useEffect(() => {
		getOverviewUrl();
	}, [items]);

	if (!items || items.length === 0) {
		const button = empty?.href
			? {
					title: empty?.title || intl.formatMessage({ id: 'modules.highlight.empty.button' }, { type: title }),
					href: empty.href,
					variant: 'primary' as IButtonVariants
			  }
			: undefined;
		const message =
			empty?.message ||
			(theme?.title
				? intl.formatMessage({ id: 'modules.highlight.empty.message.theme' }, { theme: theme?.title })
				: intl.formatMessage({ id: 'modules.highlight.empty.message' }));

		return <EmptyTab content={message} button={button} />;
	}

	return (
		<>
			<div className={clsx(styles.row, itemsHaveImages && styles.horizontal)}>
				{list?.map((item, index) => {
					const rawSlug =
						item?.slug?.url ||
						item?.slug?.linkUrl ||
						(typeof item?.slug === 'string' ? item.slug : null) ||
						item?.url ||
						'';
					// Maps/figures: avoid linking to "#" (empty link) which resolves to themas/%23 on theme pages
					const slug =
						item?.__typename === 'MapsFigure' && (!rawSlug || rawSlug === '#') ? '/kaarten-en-cijfers' : rawSlug;

					switch (item.__typename) {
						case 'Newsarticle':
							return (
								<div key={`${item?.id} ${index}`} className={styles.column}>
									<CardNews
										type="nieuws"
										id={`highlight-tab-${index}`}
										slug={slug}
										title={item?.header?.title || item?.title || ''}
										bodyText={item?.bodyText?.bodyText || ''}
										excerpt={item?.header?.excerpt || ''}
										publishedAt={item?.firstPublishedAt}
										cover={item?.header?.cover?.data?.attributes}
										hideCover={false}
										transparent
										nestedTitle
									/>
								</div>
							);
						case 'WooRequest':
							return (
								<div key={`${item?.id} ${index}`} id={`highlight-tab-${index}`} className={styles.column}>
									<CardDownload
										title={item?.title || ''}
										zip={item.zip}
										date={item.startDate || item.createdAt || ''}
									/>
								</div>
							);

						default:
							return (
								<div key={`${item?.id} ${index}`} id={`highlight-tab-${index}`} className={styles.column}>
									<Card
										slug={slug}
										title={item?.header?.title || item?.title || ''}
										cover={item?.header?.cover?.data?.attributes}
									/>
								</div>
							);
					}
				})}
			</div>

			{buttonHref && buttonTitle && (
				<Button title={buttonTitle} variant="primary" squaredSize="big" href={buttonHref} />
			)}

			{(!buttonHref || !buttonTitle) && overviewUrl && (
				<Button
					title={
						buttonTitle ||
						(theme?.title
							? intl.formatMessage({ id: 'modules.highlight.button.theme' }, { theme: theme?.title, type: title })
							: intl.formatMessage({ id: 'modules.highlight.button.more' }, { type: title?.toLowerCase() }))
					}
					variant="primary"
					squaredSize="big"
					href={overviewUrl}
					query={theme?.id ? { themeIds: theme.id } : undefined}
				/>
			)}
		</>
	);
};

export default Tab;
